import { Checkbox, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

export type LeaveApprovalSearchParams = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  periodYear?: number;
  periodMonth?: number;
  /** When true, list shows startDate >= first day of selected month (default on approvals). */
  periodFromOnly?: boolean;
  /** When true (default on approvals), filter by submitted (created) date instead of leave start date. */
  filterByCreated?: boolean;
  status?: string;
  search?: string;
};

const VALUE_ALL = "__all__";
const STATUS_OPTIONS = [
  { value: "pending_line", label: "ລໍຖ້າຫົວໜ້າສາຍ" },
  { value: "pending_hr", label: "ລໍຖ້າ HR" },
  { value: VALUE_ALL, label: "ທັງໝົດ" },
  { value: "approved", label: "ອະນຸມັດແລ້ວ" },
  { value: "rejected", label: "ປະຕິເສດ" },
];

const MONTHS = [
  { value: "1", label: "ມ.ກ." },
  { value: "2", label: "ກ.ພ." },
  { value: "3", label: "ມ.ນ." },
  { value: "4", label: "ມ.ສ." },
  { value: "5", label: "ພ.ພ." },
  { value: "6", label: "ມ.ຖ." },
  { value: "7", label: "ກ.ລ." },
  { value: "8", label: "ສ.ຫ." },
  { value: "9", label: "ກ.ຍ." },
  { value: "10", label: "ຕ.ລ." },
  { value: "11", label: "ພ.ຈ." },
  { value: "12", label: "ທ.ວ." },
];

type LeaveApprovalFilterProps = {
  searchParams: LeaveApprovalSearchParams;
  fromRoute: "/app/hr/leave/approvals" | "/app/hr/leave/history";
  /** Default status when not in URL (e.g. pending_line / pending_hr for approvals). */
  defaultStatus?: string;
  /** Default periodFromOnly (e.g. true for approvals = from month onwards). */
  defaultPeriodFromOnly?: boolean;
  /** Default filterByCreated (e.g. true for approvals = by submitted date). */
  defaultFilterByCreated?: boolean;
};

export function LeaveApprovalFilter({
  searchParams,
  fromRoute,
  defaultStatus = "pending_hr",
  defaultPeriodFromOnly = false,
  defaultFilterByCreated = false,
}: LeaveApprovalFilterProps) {
  const nav = useNavigate({ from: fromRoute });
  const search = searchParams;
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const periodFromOnly =
    search.periodFromOnly !== undefined
      ? search.periodFromOnly
      : defaultPeriodFromOnly;
  const filterByCreated =
    search.filterByCreated !== undefined
      ? search.filterByCreated
      : defaultFilterByCreated;

  const [searchInput, setSearchInput] = useState(search.search ?? "");
  const [statusInput, setStatusInput] = useState(
    search.status ?? defaultStatus,
  );
  const [yearInput, setYearInput] = useState(
    String(search.periodYear ?? currentYear),
  );
  const [monthInput, setMonthInput] = useState(
    String(search.periodMonth ?? currentMonth),
  );
  const [fromMonthOnlyInput, setFromMonthOnlyInput] = useState(periodFromOnly);
  const [filterByCreatedInput, setFilterByCreatedInput] =
    useState(filterByCreated);

  useEffect(() => {
    setSearchInput(search.search ?? "");
    setStatusInput(search.status ?? defaultStatus);
    setYearInput(String(search.periodYear ?? currentYear));
    setMonthInput(String(search.periodMonth ?? currentMonth));
    setFromMonthOnlyInput(
      search.periodFromOnly !== undefined
        ? search.periodFromOnly
        : defaultPeriodFromOnly,
    );
    setFilterByCreatedInput(
      search.filterByCreated !== undefined
        ? search.filterByCreated
        : defaultFilterByCreated,
    );
  }, [
    search.search,
    search.status,
    search.periodYear,
    search.periodMonth,
    search.periodFromOnly,
    search.filterByCreated,
    currentYear,
    currentMonth,
    defaultStatus,
    defaultPeriodFromOnly,
    defaultFilterByCreated,
  ]);

  const doNav = (
    opts: Partial<{
      year: string;
      month: string;
      status: string;
      searchVal: string;
      fromMonthOnly: boolean;
      filterByCreated: boolean;
    }> = {},
  ) => {
    const yr = opts.year ?? yearInput;
    const mon = opts.month ?? monthInput;
    const st = opts.status ?? statusInput;
    const sv = opts.searchVal ?? searchInput;
    const fmo = opts.fromMonthOnly ?? fromMonthOnlyInput;
    const fbc = opts.filterByCreated ?? filterByCreatedInput;
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        periodYear: Number(yr) || currentYear,
        periodMonth: Number(mon) || currentMonth,
        periodFromOnly: fmo,
        filterByCreated: fbc,
        status: st === VALUE_ALL ? undefined : st,
        search: sv.trim() || undefined,
      },
    });
  };

  const debouncedSearch = useDebounceCallback((val: string) => {
    doNav({ searchVal: val });
  }, 400);

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <SearchIcon className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="ຄົ້ນຫາພະນັກງານ ຫຼື ປະເພດລາ..."
            className="h-8 pl-8 sm:max-w-2xs"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              debouncedSearch(e.target.value);
            }}
          />
        </div>
        <Input
          type="number"
          min={2000}
          max={2100}
          value={yearInput}
          onChange={(e) => {
            setYearInput(e.target.value);
            doNav({ year: e.target.value });
          }}
          placeholder="ປີ"
          className="h-8 w-full sm:w-24"
        />
        <SimpleSelect
          options={MONTHS}
          value={monthInput}
          onValueChange={(v) => {
            setMonthInput(v);
            doNav({ month: v });
          }}
          placeholder="ເດືອນ"
          className="h-8 w-full sm:w-32"
        />
        <SimpleSelect
          options={STATUS_OPTIONS}
          value={statusInput}
          onValueChange={(v) => {
            setStatusInput(v);
            doNav({ status: v });
          }}
          placeholder="ສະຖານະ"
          className="h-8 w-full sm:w-32"
        />
      </div>
      {fromRoute === "/app/hr/leave/approvals" && (
        <>
          <div className="flex items-center gap-2">
            <Checkbox
              id="filter-by-created"
              checked={filterByCreatedInput}
              onCheckedChange={(v) => {
                const checked = v === true;
                setFilterByCreatedInput(checked);
                doNav({ filterByCreated: checked });
              }}
            />
            <label
              htmlFor="filter-by-created"
              className="ml-2 cursor-pointer text-muted-foreground text-sm"
            >
              ຕາມວັນທີສົ່ງ
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="period-from-only"
              checked={fromMonthOnlyInput}
              onCheckedChange={(v) => {
                const checked = v === true;
                setFromMonthOnlyInput(checked);
                doNav({ fromMonthOnly: checked });
              }}
            />
            <label
              htmlFor="period-from-only"
              className="ml-2 cursor-pointer text-muted-foreground text-sm"
            >
              ຕັ້ງແຕ່ເດືອນນີ້ເປີດຂຶ້ນໄປ
            </label>
          </div>
        </>
      )}
    </div>
  );
}
